/**
 * AI Module - Journey Generator v3.0 (Rule Engine Primary)
 *
 * Generates a complete, personalized journey using deterministic Rule Engine data.
 * This is now the PRIMARY path - not just a fallback.
 *
 * v3.0 Changes:
 * - Uses preComputedJourneyData as single source of truth
 * - Full parity with LLM-assembled output (actually better - no hallucination risk)
 * - Personalized with user's name throughout
 * - Rich insights generated from insightSeeds
 * - Complete confidence score breakdown
 * - All timeline milestones included
 * - liquidationSequence included
 *
 * User sees:
 * - Personalized hero section with their name
 * - Complete tax savings breakdown
 * - AI-quality insights (from Rule Engine insightSeeds)
 * - Full timeline with all key dates
 * - Liquidation sequence table
 * - Scenario comparison
 * - No indication this is "fallback" - it's the real deal
 */

import type { FactPack, PreComputedJourneyData } from '../schemas/factPack.schema';
import type { JourneyOutput, JourneyAction, AIInsight, TaxSavingsItem } from '../schemas/journeyOutput.schema';
import { defaults } from '@/data';

// Default exchange rate (fallback only - should come from profile in production)
const DEFAULT_EXCHANGE_RATE = defaults.profile.exchangeRate;

// Helper to format currency in lakhs/crores
function formatINRAmount(amount: number): string {
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(1)}Cr`;
  } else if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(1)}L`;
  } else {
    return `₹${amount.toLocaleString('en-IN')}`;
  }
}

// Helper to format USD amounts
function formatUSDAmount(amount: number): string {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `$${Math.round(amount / 1000)}K`;
  } else {
    return `$${amount.toLocaleString('en-US')}`;
  }
}

// Helper to generate dynamic "comparedTo" text based on tax savings categories
function generateComparedToText(items: { category: string; amountINR: number }[]): string {
  if (!items || items.length === 0) {
    return 'vs standard approach';
  }

  // Get top categories by amount (max 3)
  const sortedItems = [...items]
    .filter(item => item.amountINR > 0)
    .sort((a, b) => b.amountINR - a.amountINR);

  if (sortedItems.length === 0) {
    return 'vs standard approach';
  }

  // Category short names for display
  const categoryShortNames: Record<string, string> = {
    'RSU Capital Gains': 'RSU timing',
    'Brokerage Capital Gains': 'brokerage timing',
    'ESPP Liquidation': 'ESPP timing',
    'RNOR Foreign Income': 'RNOR benefits',
    'RNOR Passive Income': 'RNOR benefits',
    'Retirement Roth Conversion': 'Roth conversion',
    'Tax Optimization': 'tax optimization',
  };

  // Get short names for top categories
  const topCategories = sortedItems.slice(0, 3).map(item => {
    // Try exact match first
    if (categoryShortNames[item.category]) {
      return categoryShortNames[item.category];
    }
    // Try partial match
    const lowerCat = item.category.toLowerCase();
    if (lowerCat.includes('rsu')) return 'RSU timing';
    if (lowerCat.includes('brokerage')) return 'brokerage timing';
    if (lowerCat.includes('espp')) return 'ESPP timing';
    if (lowerCat.includes('rnor')) return 'RNOR benefits';
    if (lowerCat.includes('401k') || lowerCat.includes('roth')) return 'Roth conversion';
    // Default to category name
    return item.category.toLowerCase();
  });

  // Remove duplicates
  const uniqueCategories = [...new Set(topCategories)];

  if (uniqueCategories.length === 1) {
    return `via ${uniqueCategories[0]}`;
  } else if (uniqueCategories.length === 2) {
    return `via ${uniqueCategories[0]} + ${uniqueCategories[1]}`;
  } else {
    return `via ${uniqueCategories[0]}, ${uniqueCategories[1]} + more`;
  }
}

/**
 * Generate a complete journey from the Fact Pack
 * Uses preComputedJourneyData when available (preferred path)
 * Falls back to computing from factPack sections if needed
 */
export function generateFallbackJourney(factPack: FactPack): JourneyOutput {
  const preComputed = factPack.preComputedJourneyData;
  const firstName = factPack.profile.firstName || '';
  
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const returnYear = factPack.profile.targetReturnYear;
  const returnMonth = factPack.profile.targetReturnMonth ?? 4; // Default to April (FY start)

  // Calculate months until return
  const monthsUntilReturn =
    (returnYear - currentYear) * 12 + (returnMonth - currentMonth);

  // Use preComputed data if available, otherwise compute
  const taxSavings = preComputed?.taxSavings 
    ? {
        total: preComputed.taxSavings.total,
        totalFormatted: preComputed.taxSavings.totalFormatted,
        items: preComputed.taxSavings.items.map(item => ({
          category: item.category,
          amountINR: item.amountINR,
          formatted: item.amountFormatted,
          description: item.description,
          timing: item.timingDescription || item.timing || 'During optimization window',
          confidence: item.confidence as 'high' | 'medium' | 'estimated',
        }))
      }
    : calculateTaxSavings(factPack);

  // Generate AI insights from preComputed insightSeeds or factPack data
  const aiInsights = preComputed?.insightSeeds 
    ? generateInsightsFromSeeds(preComputed.insightSeeds, factPack, firstName)
    : generateFallbackInsights(factPack, firstName);

  // Build scenario comparison from preComputed or factPack
  const scenarioComparison = preComputed?.scenarios 
    ? buildScenarioFromPreComputed(preComputed.scenarios)
    : buildScenarioComparison(factPack);

  // Build phases based on profile
  const phases = buildFallbackPhases(factPack, currentYear, returnYear, preComputed);

  // Get confidence data from preComputed or compute basic version
  const confidenceData = preComputed?.confidenceScore || {
    overall: factPack.dataQuality.overallConfidence === 'high' ? 90 
           : factPack.dataQuality.overallConfidence === 'medium' ? 70 : 50,
    level: factPack.dataQuality.overallConfidence,
    components: {
      rnorCertainty: factPack.taxStrategy.rnpioBased.eligible ? 85 : 50,
      assetDataCompleteness: factPack.dataQuality.missingFields.length === 0 ? 90 : 60,
      taxCalculationAccuracy: 75,
      projectionReliability: 70,
    },
    explanation: factPack.dataQuality.confidenceExplanation,
    limitations: factPack.dataQuality.missingFields.map(f => f.field),
    improvementSuggestions: factPack.dataQuality.missingFields
      .filter(f => f.impact === 'reduces_accuracy')
      .map(f => `Add ${f.field} for better accuracy`),
  };

  // Build Social Security eligibility (USA only)
  const ssEligibility = preComputed?.socialSecurity ? {
    eligible: preComputed.socialSecurity.eligible,
    yearsToQualify: preComputed.socialSecurity.yearsToQualify,
    lifetimeValueFormatted: preComputed.socialSecurity.lifetimeValueFormatted,
    recommendation: preComputed.socialSecurity.recommendationText || 
      (preComputed.socialSecurity.eligible 
        ? 'You qualify for Social Security benefits!' 
        : `${preComputed.socialSecurity.yearsToQualify} more years of US work to qualify`),
    isHighValueOpportunity: preComputed.socialSecurity.yearsToQualify <= 3 && 
                            preComputed.socialSecurity.lifetimeValueINR > 10000000,
  } : undefined;

  // Build liquidation sequence from preComputed
  const liquidationSequence = preComputed?.liquidationSequence?.map(item => ({
    id: item.id,
    sequence: item.sequence,
    asset: item.asset,
    action: item.action,
    timing: item.timing,
    timingDescription: item.timingDescription,
    reason: item.reason,
    estimatedValueUSD: item.estimatedValueUSD,
    estimatedValueINR: item.estimatedValueINR,
    taxImplication: item.taxImplication,
    requiresCAReview: item.requiresCAReview,
  })) || [];

  // Build comprehensive timeline with all dates
  const timeline = buildTimeline(factPack, preComputed, currentMonth, currentYear, returnMonth, returnYear, monthsUntilReturn);

  // Generate personalized headline based on situation
  const personalizedHeadline = generatePersonalizedHeadline(factPack, firstName, taxSavings.total);
  const personalizedSubheadline = generatePersonalizedSubheadline(factPack, preComputed);

  // Generate dynamic "comparedTo" text based on actual tax savings categories
  const comparedToText = generateComparedToText(taxSavings.items);

  return {
    schemaVersion: '2.0',
    generatedAt: new Date().toISOString(),

    // Hero stats for the "wow" moment - FULLY POPULATED
    heroStats: {
      totalTaxSavings: {
        amountINR: taxSavings.total,
        formatted: taxSavings.totalFormatted || formatINRAmount(taxSavings.total),
        comparedTo: comparedToText,
      },
      timeToFIRE: {
        months: preComputed?.heroStats?.daysUntilMove 
          ? Math.round(preComputed.heroStats.daysUntilMove / 30)
          : Math.round(factPack.fireSnapshot.monthsToFIRE),
        formatted: factPack.fireSnapshot.fireStatus === 'achieved' 
          ? 'Already achieved!' 
          : `${Math.round(factPack.fireSnapshot.monthsToFIRE)} months`,
        status: factPack.fireSnapshot.fireStatus === 'achieved' 
          ? 'achieved' 
          : factPack.fireSnapshot.fireStatus === 'on_track' ? 'on_track' : 'at_risk',
      },
      confidence: {
        score: confidenceData.overall,
        level: confidenceData.level,
        explanation: confidenceData.explanation,
      },
      rnorBenefit: {
        eligible: factPack.taxStrategy.rnpioBased.eligible,
        years: factPack.taxStrategy.rnpioBased.rnorYears,
        impactDescription: factPack.taxStrategy.rnpioBased.eligible
          ? `${factPack.taxStrategy.rnpioBased.rnorYears}-year tax-free foreign income window`
          : 'RNOR benefits not applicable',
      },
      // NEW: Social Security eligibility (USA only)
      ssEligibility,
      personalizedHeadline,
      personalizedSubheadline,
    },

    // Tax savings breakdown
    taxSavingsHighlight: {
      totalSavingsINR: taxSavings.total,
      totalFormatted: taxSavings.totalFormatted || formatINRAmount(taxSavings.total),
      savingsBreakdown: taxSavings.items.length > 0 ? taxSavings.items : [{
        category: 'Tax Optimization',
        amountINR: taxSavings.total,
        formatted: formatINRAmount(taxSavings.total),
        description: 'Potential savings through strategic planning',
        timing: 'Throughout your journey',
        confidence: 'estimated' as const,
      }],
      methodology: 'Based on RNOR tax benefits and optimized liquidation timing',
      disclaimer: 'Tax savings estimates based on current laws. Consult a CA for precise calculations.',
    },

    // AI insights - rich and personalized
    aiInsights,

    // Scenario comparison
    scenarioComparison,

    // NEW: Liquidation sequence
    liquidationSequence,

    // Summary - personalized, NOT "template"
    summary: {
      headline: personalizedHeadline,
      subheadline: personalizedSubheadline,
      confidenceLevel: confidenceData.level,
      confidenceExplanation: confidenceData.explanation,
    },

    // Timeline - complete with all dates
    timeline,

    // Phases with actions
    phases,

    // Key decision points
    keyDecisionPoints: buildFallbackDecisionPoints(factPack, firstName),

    // Assumptions
    assumptions: factPack.dataQuality.assumptions.map((a) => ({
      category: a.category,
      assumption: a.assumption,
      source: 'system_default' as const,
    })),

    // Disclaimers - professional, not "fallback"
    disclaimers: {
      standard:
        'This analysis is for informational purposes. Tax laws are complex and subject to change. Please consult a qualified Chartered Accountant (CA) for personalized advice.',
      itemsRequiringCAReview: extractCAItems(factPack),
      dataLimitations: confidenceData.limitations.length > 0 
        ? confidenceData.limitations.map(l => `Missing: ${l}`)
        : ['All key data provided - high confidence analysis'],
    },

    // Internal flag - can be used to track but not shown to user
    _isFallback: false, // Not really a fallback anymore - it's the primary engine!
  };
}

/**
 * Generate personalized headline based on user's situation
 * MORE personalized than LLM - covers 10+ specific scenarios
 */
function generatePersonalizedHeadline(
  factPack: FactPack, 
  firstName: string,
  totalSavings: number
): string {
  const greeting = firstName ? `${firstName}, ` : '';
  const country = factPack.profile.currentCountry;
  const rnor = factPack.taxStrategy.rnpioBased;
  const fire = factPack.fireSnapshot;
  const returnYear = factPack.profile.targetReturnYear;
  const currentYear = new Date().getFullYear();
  const monthsUntilReturn = (returnYear - currentYear) * 12;
  
  // 1. FIRE achieved - celebrate!
  if (fire.fireStatus === 'achieved') {
    if (rnor.eligible) {
      return `${greeting}You're FIRE-Ready with ${rnor.rnorYears} Years of Tax-Free Income! 🎉`;
    }
    return `${greeting}You're Financially Ready for India! 🎉`;
  }
  
  // 2. Significant tax savings identified (> 25L)
  if (totalSavings > 2500000) {
    return `${greeting}I Found ${formatINRAmount(totalSavings)} in Tax Savings for You`;
  }
  
  // 3. Return is imminent (< 6 months) - create urgency
  if (monthsUntilReturn <= 6 && monthsUntilReturn > 0) {
    return `${greeting}Your ${returnYear} Return: ${monthsUntilReturn} Months to Optimize`;
  }
  
  // 4. RNOR eligible with good window
  if (rnor.eligible && rnor.rnorYears >= 2) {
    return `${greeting}Your ${rnor.rnorYears}-Year RNOR Tax Optimization Strategy`;
  }
  
  // 5. NOT RNOR eligible - address it directly
  if (!rnor.eligible && rnor.eligibilityReason) {
    // Check if they're close to eligibility
    const yearsNeeded = rnor.eligibilityReason.match(/(\d+)\s*more\s*year/i);
    if (yearsNeeded && yearsNeeded[1] && parseInt(yearsNeeded[1]) <= 2) {
      return `${greeting}${yearsNeeded[1]} More Years Could Unlock Major Tax Benefits`;
    }
    return `${greeting}Your Strategic ${country} → India Transition Plan`;
  }
  
  // 6. Strong FIRE progress (70%+)
  if (fire.progressPercent >= 70) {
    return `${greeting}At ${Math.round(fire.progressPercent)}% to FIRE - Here's Your Finish Line`;
  }
  
  // 7. UAE user - different messaging (no income tax there)
  if (country === 'UAE') {
    return `${greeting}Your UAE → India Transition: Maximize RNOR Benefits`;
  }
  
  // 8. Young person with time (return > 3 years away)
  if (monthsUntilReturn > 36) {
    return `${greeting}Your ${returnYear} Return: Building Your India-Ready Portfolio`;
  }
  
  // 9. Default - still personalized with year
  return `${greeting}Your ${country} → India Journey (Target: ${returnYear})`;
}

/**
 * Generate personalized subheadline
 * Highly contextual based on user's unique situation
 */
function generatePersonalizedSubheadline(
  factPack: FactPack,
  preComputed?: PreComputedJourneyData
): string {
  const rnor = factPack.taxStrategy.rnpioBased;
  const fire = factPack.fireSnapshot;
  const ss = preComputed?.socialSecurity;
  const country = factPack.profile.currentCountry;
  const returnYear = factPack.profile.targetReturnYear;
  
  // 1. Social Security opportunity is a BIG deal - highlight it
  if (ss && !ss.eligible && ss.yearsToQualify <= 3 && ss.yearsToQualify > 0) {
    return `⚠️ ${ss.yearsToQualify} more year${ss.yearsToQualify > 1 ? 's' : ''} of US work unlocks ${ss.lifetimeValueFormatted || '~$200K+'} in lifetime Social Security`;
  }
  
  // 2. Use preComputed insights for specific opportunities
  if (preComputed?.insightSeeds && preComputed.insightSeeds.length > 0) {
    const topInsight = preComputed.insightSeeds[0];
    if (topInsight && topInsight.amountFormatted && topInsight.type === 'opportunity') {
      return `Key Opportunity: ${topInsight.title} (${topInsight.amountFormatted})`;
    }
  }
  
  // 3. FIRE achieved + RNOR - winning combination
  if (fire.fireStatus === 'achieved' && rnor.eligible) {
    return `Leverage your ${rnor.rnorYears}-year RNOR window to optimize your ${formatINRAmount(fire.currentNetWorth)} portfolio`;
  }
  
  // 4. FIRE achieved but NOT RNOR eligible
  if (fire.fireStatus === 'achieved' && !rnor.eligible) {
    return `You're financially ready! Focus on smart asset transition to minimize tax impact`;
  }
  
  // 5. RNOR eligible - emphasize the opportunity
  if (rnor.eligible) {
    const taxSavings = factPack.taxStrategy.estimatedTotalTaxSavings_INR;
    if (taxSavings > 0) {
      return `Your ${rnor.rnorYears}-year RNOR window could save ${formatINRAmount(taxSavings)} in taxes`;
    }
    return `Optimizing your ${rnor.rnorYears}-year RNOR window for maximum tax efficiency`;
  }
  
  // 6. NOT RNOR eligible - provide constructive guidance
  if (!rnor.eligible) {
    // UAE has no income tax - different value prop
    if (country === 'UAE') {
      return `Focus on RNOR timing and NRE account strategy for tax-free fund transfers`;
    }
    // USA - mention what they can still optimize
    if (country === 'USA') {
      return `Focus on 401(k) strategy, capital gains timing, and DTAA benefits`;
    }
  }
  
  // 7. Good FIRE progress (70%+)
  if (fire.progressPercent >= 70) {
    return `At ${Math.round(fire.progressPercent)}% of FIRE goal (${formatINRAmount(fire.currentNetWorth)} of ${formatINRAmount(fire.fireNumber)})`;
  }
  
  // 8. Moderate progress (40-70%)
  if (fire.progressPercent >= 40) {
    const yearsToFire = Math.ceil(fire.yearsToFIRE);
    return `${Math.round(fire.progressPercent)}% to FIRE - ${yearsToFire} year${yearsToFire > 1 ? 's' : ''} to go with optimized strategy`;
  }
  
  // 9. UAE specific default
  if (country === 'UAE') {
    return `Tax-free UAE income + strategic India return = optimized wealth transfer`;
  }
  
  // 10. Default with year
  return `Your personalized ${returnYear} return strategy with tax optimization`;
}

/**
 * Generate rich insights from preComputed insightSeeds
 */
function generateInsightsFromSeeds(
  seeds: PreComputedJourneyData['insightSeeds'],
  factPack: FactPack,
  firstName: string
): AIInsight[] {
  const insights: AIInsight[] = [];
  const greeting = firstName ? `${firstName}, ` : '';
  
  seeds.forEach((seed, index) => {
    const insight: AIInsight = {
      id: `insight_${index + 1}`,
      type: seed.type === 'opportunity' ? 'opportunity' 
          : seed.type === 'warning' ? 'warning' 
          : 'tip',
      title: seed.title,
      insight: seed.keyData,
      specificAmount: seed.amountFormatted,
      relatedAsset: seed.relatedAsset,
      actionRequired: seed.type === 'opportunity' || seed.type === 'warning',
      urgency: seed.urgency,
    };
    
    // Enhance insights with personalization
    if (seed.type === 'opportunity' && seed.amountFormatted) {
      insight.title = `${greeting}${seed.title}`;
    }
    
    insights.push(insight);
  });
  
  // Ensure we have at least 3 insights
  if (insights.length < 3) {
    // Add RNOR confirmation if eligible
    if (factPack.taxStrategy.rnpioBased.eligible && !insights.some(i => i.id.includes('rnor'))) {
      insights.push({
        id: 'insight_rnor_confirm',
        type: 'confirmation',
        title: 'RNOR Eligibility Confirmed ✓',
        insight: `You qualify for ${factPack.taxStrategy.rnpioBased.rnorYears} years of RNOR status. Foreign income won't be taxable in India during this window.`,
        actionRequired: false,
      });
    }
    
    // Add FIRE status
    if (insights.length < 3 && factPack.fireSnapshot.fireStatus === 'achieved') {
      insights.push({
        id: 'insight_fire_achieved',
        type: 'confirmation',
        title: 'FIRE Goal Achieved! 🎉',
        insight: `Your net worth of ${formatINRAmount(factPack.fireSnapshot.currentNetWorth)} exceeds your FIRE target. You're financially ready.`,
        specificAmount: formatINRAmount(factPack.fireSnapshot.currentNetWorth),
        actionRequired: false,
      });
    }
    
    // Add DTAA tip
    if (insights.length < 3 && factPack.taxStrategy.dtaa.applicable) {
      insights.push({
        id: 'insight_dtaa',
        type: 'tip',
        title: 'DTAA Protection Active',
        insight: `India-${factPack.taxStrategy.dtaa.treatyCountry} tax treaty protects you from double taxation.`,
        actionRequired: false,
      });
    }
  }
  
  return insights.slice(0, 5); // Max 5 insights
}

/**
 * Build scenario comparison from preComputed data
 */
function buildScenarioFromPreComputed(
  scenarios: PreComputedJourneyData['scenarios']
): JourneyOutput['scenarioComparison'] {
  return {
    scenarios: scenarios.scenarios.map(s => ({
      id: s.id,
      name: s.name,
      returnDate: s.returnDate,
      returnFY: s.returnFY,
      isRecommended: s.isRecommended,
      isCurrentPlan: s.isCurrentPlan,
      metrics: {
        rnorYears: s.metrics.rnorYears,
        taxSavingsINR: s.metrics.taxSavingsINR,
        taxSavingsFormatted: s.metrics.taxSavingsFormatted,
        additionalCorpusINR: s.metrics.additionalCorpusINR,
        additionalCorpusFormatted: s.metrics.additionalCorpusFormatted,
        // Map from scenario type to risk level
        riskLevel: s.type === 'conservative' ? 'low' : s.type === 'aggressive' ? 'high' : 'medium',
        riskExplanation: s.summary || `${s.type} approach to return timing`,
      },
      pros: s.pros,
      cons: s.cons,
    })),
    recommendedScenarioId: scenarios.recommendedScenarioId,
    recommendationRationale: scenarios.recommendationRationale,
    keyTradeoff: scenarios.keyTradeoff,
  };
}

/**
 * Build comprehensive timeline with all dates from preComputed
 */
function buildTimeline(
  factPack: FactPack,
  preComputed: PreComputedJourneyData | undefined,
  currentMonth: number,
  currentYear: number,
  returnMonth: number,
  returnYear: number,
  monthsUntilReturn: number
): JourneyOutput['timeline'] {
  const dates = preComputed?.dates;
  const milestones = preComputed?.milestones;
  
  // Build milestones from preComputed or create basic ones
  const timelineMilestones = milestones 
    ? milestones.map(m => ({
        id: m.id,
        date: m.date,
        label: m.title,
        type: mapMilestoneCategory(m.category),
        description: m.description,
      }))
    : [
        {
          id: 'milestone_start',
          date: formatMonthYear(currentMonth, currentYear),
          label: 'Today',
          type: 'start' as const,
          description: 'Journey planning begins',
        },
        {
          id: 'milestone_return',
          date: formatMonthYear(returnMonth, returnYear),
          label: 'Return Day',
          type: 'return' as const,
          description: 'Target return to India',
        },
        ...(factPack.taxStrategy.rnpioBased.eligible ? [{
          id: 'milestone_rnor_end',
          date: factPack.taxStrategy.rnpioBased.rnorEndDate,
          label: 'RNOR Ends',
          type: 'rnor_end' as const,
          description: 'Foreign income becomes taxable',
        }] : []),
      ];

  return {
    startDate: dates?.currentDateFormatted || formatMonthYear(currentMonth, currentYear),
    targetReturnDate: dates?.targetMoveDateFormatted || formatMonthYear(returnMonth, returnYear),
    targetReturnFY: dates?.fiscalYearOfReturn || `FY ${returnYear}-${(returnYear + 1).toString().slice(-2)}`,
    // NEW: RNOR start date (parity with LLM assembler)
    rnorStartDate: dates?.rnorStartDateFormatted ||
      (factPack.taxStrategy.rnpioBased.eligible ? factPack.taxStrategy.rnpioBased.rnorStartDate : undefined),
    rnorEndDate: dates?.rnorEndDateFormatted || 
      (factPack.taxStrategy.rnpioBased.eligible ? factPack.taxStrategy.rnpioBased.rnorEndDate : undefined),
    // NEW: Key milestone dates (parity with LLM assembler)
    sixMonthsBefore: dates?.sixMonthsBeforeMoveFormatted,
    threeMonthsBefore: dates?.threeMonthsBeforeMoveFormatted,
    fireTargetDate:
      factPack.fireSnapshot.fireStatus !== 'achieved'
        ? `${currentYear + Math.ceil(factPack.fireSnapshot.yearsToFIRE)}`
        : undefined,
    totalMonths: dates?.monthsUntilMove || Math.max(0, monthsUntilReturn),
    milestones: timelineMilestones,
  };
}

/**
 * Map milestone category to timeline type
 */
function mapMilestoneCategory(
  category: 'preparation' | 'transition' | 'rnor' | 'post_rnor' | 'compliance'
): 'start' | 'return' | 'rnor_end' | 'fire_target' | 'milestone' {
  const mapping: Record<string, 'start' | 'return' | 'rnor_end' | 'fire_target' | 'milestone'> = {
    preparation: 'milestone',
    transition: 'return',
    rnor: 'rnor_end',
    post_rnor: 'milestone',
    compliance: 'milestone',
  };
  return mapping[category] || 'milestone';
}

/**
 * Calculate tax savings from fact pack data (fallback when no preComputed)
 */
function calculateTaxSavings(factPack: FactPack): { 
  total: number; 
  totalFormatted: string;
  items: TaxSavingsItem[] 
} {
  const items: TaxSavingsItem[] = [];
  let total = 0;

  // RNOR foreign income benefit
  if (factPack.taxStrategy.rnpioBased.eligible && factPack.taxStrategy.rnpioBased.impactLevel === 'high') {
    const foreignAssets = factPack.assets.residentCountry.total;
    const estimatedForeignIncome = foreignAssets * 0.035; // ~3.5% SWR
    const rnorYears = factPack.taxStrategy.rnpioBased.rnorYears;
    const estimatedSavings = estimatedForeignIncome * 0.30 * rnorYears * DEFAULT_EXCHANGE_RATE;
    
    if (estimatedSavings > 0) {
      items.push({
        category: 'RNOR Foreign Income',
        amountINR: Math.round(estimatedSavings),
        formatted: formatINRAmount(Math.round(estimatedSavings)),
        description: `Tax-free foreign income during ${rnorYears}-year RNOR window`,
        timing: `${factPack.taxStrategy.rnpioBased.rnorStartDate} - ${factPack.taxStrategy.rnpioBased.rnorEndDate}`,
        confidence: 'estimated',
      });
      total += Math.round(estimatedSavings);
    }
  }

  // Liquidation plan savings
  factPack.taxStrategy.liquidationPlan.forEach((item) => {
    if (item.estimatedTaxSavings_INR > 0) {
      items.push({
        category: item.asset,
        amountINR: item.estimatedTaxSavings_INR,
        formatted: formatINRAmount(item.estimatedTaxSavings_INR),
        description: item.taxImplication,
        timing: item.recommendedTiming,
        confidence: item.confidence === 'low' ? 'estimated' : item.confidence as 'high' | 'medium' | 'estimated',
      });
      total += item.estimatedTaxSavings_INR;
    }
  });

  // Use pre-calculated total if higher
  if (factPack.taxStrategy.estimatedTotalTaxSavings_INR > total) {
    total = factPack.taxStrategy.estimatedTotalTaxSavings_INR;
  }

  return { total, totalFormatted: formatINRAmount(total), items };
}

/**
 * Generate fallback AI insights (when no insightSeeds available)
 */
function generateFallbackInsights(factPack: FactPack, firstName: string): AIInsight[] {
  const insights: AIInsight[] = [];
  const greeting = firstName ? `${firstName}, ` : '';

  // RNOR insight
  if (factPack.taxStrategy.rnpioBased.eligible) {
    insights.push({
      id: 'insight_rnor',
      type: 'confirmation',
      title: `${greeting}RNOR Eligibility Confirmed`,
      insight: `You qualify for ${factPack.taxStrategy.rnpioBased.rnorYears} years of RNOR status. Foreign income won't be taxable in India during this window - the foundation of your tax strategy.`,
      actionRequired: false,
    });
  } else {
    insights.push({
      id: 'insight_rnor_not_eligible',
      type: 'warning',
      title: 'RNOR Not Applicable',
      insight: factPack.taxStrategy.rnpioBased.eligibilityReason,
      actionRequired: false,
    });
  }

  // FIRE status insight
  if (factPack.fireSnapshot.fireStatus === 'achieved') {
    insights.push({
      id: 'insight_fire_achieved',
      type: 'confirmation',
      title: `${greeting}FIRE Goal Achieved! 🎉`,
      insight: `Your net worth of ${formatINRAmount(factPack.fireSnapshot.currentNetWorth)} exceeds your FIRE target of ${formatINRAmount(factPack.fireSnapshot.fireNumber)}. You're financially ready for the move.`,
      specificAmount: formatINRAmount(factPack.fireSnapshot.currentNetWorth),
      actionRequired: false,
    });
  } else if (factPack.fireSnapshot.progressPercent >= 70) {
    insights.push({
      id: 'insight_fire_progress',
      type: 'tip',
      title: 'Strong FIRE Progress',
      insight: `At ${Math.round(factPack.fireSnapshot.progressPercent)}% of your FIRE goal, you're on track. Consider timing your return to maximize both corpus building and RNOR benefits.`,
      specificAmount: `${Math.round(factPack.fireSnapshot.progressPercent)}%`,
      actionRequired: false,
    });
  }

  // Retirement accounts insight
  const retirementTotal = factPack.assets.residentCountry.breakdown.retirement;
  if (retirementTotal > 0) {
    insights.push({
      id: 'insight_retirement',
      type: 'opportunity',
      title: `${greeting}Retirement Account Opportunity`,
      insight: `Your ${formatUSDAmount(retirementTotal)} in retirement accounts can be strategically converted during RNOR for significant tax savings.`,
      specificAmount: formatINRAmount(retirementTotal * DEFAULT_EXCHANGE_RATE),
      relatedAsset: '401k/IRA',
      actionRequired: true,
      urgency: 'during_rnor',
    });
  }

  // DTAA insight
  if (factPack.taxStrategy.dtaa.applicable) {
    insights.push({
      id: 'insight_dtaa',
      type: 'tip',
      title: 'DTAA Protection Active',
      insight: `The India-${factPack.taxStrategy.dtaa.treatyCountry} tax treaty protects you from double taxation. Key benefits: ${factPack.taxStrategy.dtaa.keyBenefits.slice(0, 2).join(', ')}.`,
      actionRequired: false,
    });
  }

  return insights.slice(0, 5);
}

/**
 * Build scenario comparison from fact pack (when no preComputed)
 */
function buildScenarioComparison(factPack: FactPack): JourneyOutput['scenarioComparison'] {
  const scenarios = factPack.workScenario.alternativeScenarios;
  
  if (scenarios.length < 1) {
    return undefined;
  }

  const currentYear = factPack.workScenario.currentPlan.returnYear;
  const currentMonth = factPack.workScenario.currentPlan.returnMonth ?? 4;

  return {
    scenarios: [
      {
        id: 'scenario_current',
        name: 'Current Plan',
        returnDate: formatMonthYear(currentMonth, currentYear),
        returnFY: `FY ${currentYear}-${(currentYear + 1).toString().slice(-2)}`,
        isRecommended: factPack.workScenario.recommendedPath === 'return_as_planned',
        isCurrentPlan: true,
        metrics: {
          rnorYears: factPack.taxStrategy.rnpioBased.rnorYears,
          taxSavingsINR: factPack.taxStrategy.estimatedTotalTaxSavings_INR,
          taxSavingsFormatted: formatINRAmount(factPack.taxStrategy.estimatedTotalTaxSavings_INR),
          additionalCorpusINR: 0,
          additionalCorpusFormatted: '₹0',
          riskLevel: 'low',
          riskExplanation: 'Following your planned timeline',
        },
        pros: ['On your planned timeline', 'Predictable transition'],
        cons: [],
      },
      ...scenarios.slice(0, 2).map((scenario, index) => ({
        id: `scenario_alt_${index + 1}`,
        name: scenario.name,
        returnDate: scenario.returnDate,
        returnFY: `FY ${scenario.returnDate.split(' ')[1]}-${(parseInt(scenario.returnDate.split(' ')[1] || '0') + 1).toString().slice(-2)}`,
        isRecommended: false,
        isCurrentPlan: false,
        metrics: {
          rnorYears: factPack.taxStrategy.rnpioBased.rnorYears,
          taxSavingsINR: 0,
          taxSavingsFormatted: 'TBD',
          additionalCorpusINR: 0,
          additionalCorpusFormatted: scenario.fireImpact,
          riskLevel: 'medium' as const,
          riskExplanation: scenario.taxImpact,
        },
        pros: [scenario.fireImpact],
        cons: [scenario.taxImpact],
      })),
    ],
    recommendedScenarioId: 'scenario_current',
    recommendationRationale: 'Your current plan balances tax optimization with your timeline goals.',
    keyTradeoff: 'Earlier return = more RNOR years but less corpus. Later return = more savings but delayed benefits.',
  };
}

/**
 * Build phases for journey
 */
function buildFallbackPhases(
  factPack: FactPack,
  currentYear: number,
  returnYear: number,
  preComputed?: PreComputedJourneyData
): JourneyOutput['phases'] {
  const phases: JourneyOutput['phases'] = [];
  const hasRNOR =
    factPack.taxStrategy.rnpioBased.eligible &&
    factPack.taxStrategy.rnpioBased.impactLevel !== 'none';

  // Use preComputed dates for better specificity
  const dates = preComputed?.dates;
  const sixMonthsBefore = dates?.sixMonthsBeforeMoveFormatted || `Mid ${returnYear - 1}`;

  // Phase 1: Pre-Return Preparation
  const preReturnActions = factPack.priorityActions
    .map((action, originalIndex) => ({ action, originalIndex }))
    .filter(({ action }) => action.timing === 'before_return' || action.timing === 'immediate')
    .slice(0, 5)
    .map(({ action, originalIndex }, i) => mapToJourneyAction(action, i + 1, 'phase_1', originalIndex));

  const phase1Savings = preReturnActions.reduce((sum, a) => sum + (a.estimatedSavingsINR || 0), 0);

  phases.push({
    id: 'phase_1_pre_return',
    sequence: 1,
    name: 'Pre-Return Preparation',
    dateRange: {
      start: dates?.currentDateFormatted || `${currentYear}`,
      end: sixMonthsBefore,
    },
    description:
      'Prepare financially and administratively. Open NRE accounts, review investments, and plan asset transitions.',
    status: 'current',
    actions:
      preReturnActions.length > 0
        ? preReturnActions
        : [createDefaultAction('phase_1', 1, 'Review financial situation')],
    totalActions: preReturnActions.length || 1,
    criticalActions: preReturnActions.filter(a => a.priority === 'critical').length,
    estimatedSavingsINR: phase1Savings,
  });

  // Phase 2: Transition (final 6 months)
  const preReturnTitles = new Set(preReturnActions.map(a => a.title));
  const transitionActions = factPack.priorityActions
    .map((action, originalIndex) => ({ action, originalIndex }))
    .filter(({ action }) => action.timing === 'before_return')
    .filter(({ action }) => !preReturnTitles.has(action.title))
    .slice(0, 4)
    .map(({ action, originalIndex }, i) => mapToJourneyAction(action, i + 1, 'phase_2', originalIndex));

  phases.push({
    id: 'phase_2_transition',
    sequence: 2,
    name: 'Final Transition',
    dateRange: {
      start: sixMonthsBefore,
      end: dates?.targetMoveDateFormatted || `April ${returnYear}`,
    },
    description:
      'Execute liquidation plan, transfer funds, finalize logistics. Critical tax decisions happen now.',
    status: 'upcoming',
    actions:
      transitionActions.length > 0
        ? transitionActions
        : [createDefaultAction('phase_2', 1, 'Execute transition plan')],
    totalActions: transitionActions.length || 1,
    criticalActions: transitionActions.filter(a => a.priority === 'critical').length,
  });

  // Phase 3: RNOR Window (if applicable)
  if (hasRNOR) {
    const rnorActions = factPack.priorityActions
      .map((action, originalIndex) => ({ action, originalIndex }))
      .filter(({ action }) => action.timing === 'during_rnor')
      .slice(0, 4)
      .map(({ action, originalIndex }, i) => mapToJourneyAction(action, i + 1, 'phase_3', originalIndex));

    // Add liquidation plan items for RNOR phase
    const liquidationActions = factPack.taxStrategy.liquidationPlan
      .map((item, originalIndex) => ({ item, originalIndex }))
      .filter(({ item }) => item.recommendedTiming.toLowerCase().includes('rnor'))
      .slice(0, 3)
      .map(({ item, originalIndex }, i) => ({
        id: `phase_3_liq_${i + 1}`,
        sequence: rnorActions.length + i + 1,
        title: item.recommendedAction,
        description: item.taxImplication,
        rationale: `Optimized timing: ${item.sourceRule}`,
        category: 'tax' as const,
        priority: 'high' as const,
        suggestedTiming: item.recommendedTiming,
        isTimeSensitive: true,
        requiresCAReview: item.requiresCAReview,
        caReviewReason: item.caReviewReason,
        factPackSource: `liquidationPlan[${originalIndex}]`,
        estimatedSavingsINR: item.estimatedTaxSavings_INR,
        estimatedSavingsFormatted: formatINRAmount(item.estimatedTaxSavings_INR),
      }));

    const allPhase3Actions = [...rnorActions, ...liquidationActions];
    const phase3Savings = allPhase3Actions.reduce((sum, a) => sum + (a.estimatedSavingsINR || 0), 0);

    phases.push({
      id: 'phase_3_rnor',
      sequence: 3,
      name: 'RNOR Tax Optimization Window',
      dateRange: {
        start: dates?.rnorStartDateFormatted || factPack.taxStrategy.rnpioBased.rnorStartDate,
        end: dates?.rnorEndDateFormatted || factPack.taxStrategy.rnpioBased.rnorEndDate,
      },
      description: `Your ${factPack.taxStrategy.rnpioBased.rnorYears}-year golden window. Foreign income is NOT taxable in India. Optimize conversions and liquidations.`,
      status: 'future',
      actions:
        allPhase3Actions.length > 0
          ? allPhase3Actions
          : [createDefaultAction('phase_3', 1, 'Review RNOR optimization opportunities')],
      totalActions: allPhase3Actions.length || 1,
      criticalActions: allPhase3Actions.filter(a => a.priority === 'critical').length,
      estimatedSavingsINR: phase3Savings,
    });
  }

  // Phase 4: Post-RNOR / Settled
  const ongoingActions = factPack.priorityActions
    .map((action, originalIndex) => ({ action, originalIndex }))
    .filter(({ action }) => action.timing === 'post_rnor' || action.timing === 'ongoing')
    .slice(0, 3)
    .map(({ action, originalIndex }, i) => mapToJourneyAction(action, i + 1, 'phase_4', originalIndex));

  phases.push({
    id: 'phase_4_settled',
    sequence: phases.length + 1,
    name: hasRNOR ? 'Post-RNOR Life' : 'Settled in India',
    dateRange: {
      start: hasRNOR 
        ? (dates?.rnorEndDateFormatted || factPack.taxStrategy.rnpioBased.rnorEndDate)
        : `${returnYear}`,
      end: 'Ongoing',
    },
    description:
      'Long-term financial management as a full India tax resident. Annual compliance and portfolio optimization.',
    status: 'future',
    actions:
      ongoingActions.length > 0
        ? ongoingActions
        : [createDefaultAction('phase_4', 1, 'Establish annual review cadence')],
    totalActions: ongoingActions.length || 1,
    criticalActions: ongoingActions.filter(a => a.priority === 'critical').length,
  });

  return phases;
}

/**
 * Map a priority action to a journey action
 */
function mapToJourneyAction(
  action: FactPack['priorityActions'][0],
  sequence: number,
  phaseId: string,
  originalIndex: number
): JourneyAction {
  return {
    id: `${phaseId}_action_${sequence}`,
    sequence,
    title: action.title,
    description: action.description,
    rationale: action.estimatedBenefit ?? 'Recommended based on your profile.',
    category: mapCategory(action.category),
    priority: action.impact === 'critical' ? 'critical' : action.impact,
    suggestedTiming: formatTiming(action.timing),
    deadline: action.deadline,
    isTimeSensitive: action.timing === 'immediate',
    requiresCAReview: action.requiresCAReview,
    caReviewReason: action.caReviewReason,
    sourceReference: action.sourceRule,
    factPackSource: `priorityActions[${originalIndex}]`,
    impactDescription: action.estimatedBenefit,
  };
}

/**
 * Map fact pack action category to journey action category
 */
function mapCategory(
  category: FactPack['priorityActions'][0]['category']
): JourneyAction['category'] {
  const mapping: Record<string, JourneyAction['category']> = {
    tax_optimization: 'tax',
    asset_rebalancing: 'investment',
    compliance: 'compliance',
    account_setup: 'account',
    insurance: 'insurance',
    estate_planning: 'documentation',
    timing: 'timing',
  };
  return mapping[category] ?? 'documentation';
}

/**
 * Format timing for display
 */
function formatTiming(timing: string): string {
  const mapping: Record<string, string> = {
    immediate: 'As soon as possible',
    before_return: 'Before your return',
    during_rnor: 'During RNOR window',
    post_rnor: 'After RNOR ends',
    ongoing: 'Ongoing',
  };
  return mapping[timing] ?? timing;
}

/**
 * Create a default action when no specific actions exist
 */
function createDefaultAction(
  phaseId: string,
  sequence: number,
  title: string
): JourneyAction {
  return {
    id: `${phaseId}_default_${sequence}`,
    sequence,
    title,
    description: 'Review your dashboard for detailed analysis.',
    rationale: 'Standard planning step.',
    category: 'documentation',
    priority: 'medium',
    suggestedTiming: 'At your convenience',
    isTimeSensitive: false,
    requiresCAReview: false,
    factPackSource: 'generated',
  };
}

/**
 * Build fallback decision points with personalization
 */
function buildFallbackDecisionPoints(
  factPack: FactPack,
  firstName: string
): JourneyOutput['keyDecisionPoints'] {
  const points: JourneyOutput['keyDecisionPoints'] = [];
  const greeting = firstName ? `${firstName}, ` : '';

  // Return date decision
  if (factPack.workScenario.optimalPlan) {
    points.push({
      id: 'decision_return_date',
      title: `${greeting}Return Timing Decision`,
      description: `Current plan: ${factPack.workScenario.currentPlan.returnYear}. Alternative: ${factPack.workScenario.optimalPlan.returnYear}. ${factPack.workScenario.optimalPlan.rationale}`,
      suggestedDecisionDate: `${factPack.workScenario.currentPlan.returnYear - 1}`,
      requiresProfessionalReview: true,
      reviewType: 'ca',
      factPackSource: 'workScenario',
      impactIfDelayed: 'Timing affects RNOR benefits worth lakhs',
    });
  }

  // RNOR strategy decision
  if (
    factPack.taxStrategy.rnpioBased.eligible &&
    factPack.taxStrategy.rnpioBased.impactLevel === 'high'
  ) {
    points.push({
      id: 'decision_rnor_strategy',
      title: 'RNOR Optimization Strategy',
      description:
        'Plan which foreign assets to liquidate during RNOR for maximum tax efficiency. This is your most impactful decision.',
      suggestedDecisionDate: factPack.taxStrategy.rnpioBased.rnorStartDate,
      requiresProfessionalReview: true,
      reviewType: 'ca',
      factPackSource: 'taxStrategy.rnpioBased',
      impactIfDelayed: 'Could cost significant tax savings',
    });
  }

  // Investment liquidation decision
  if (factPack.assets.residentCountry.breakdown.taxableInvestments > 50000) {
    points.push({
      id: 'decision_investment_liquidation',
      title: 'Investment Liquidation Strategy',
      description: `${formatUSDAmount(factPack.assets.residentCountry.breakdown.taxableInvestments)} in taxable investments. Decide: liquidate before return, during RNOR, or gradual transition.`,
      suggestedDecisionDate: `${factPack.profile.targetReturnYear - 1}`,
      options: [
        'Liquidate before return (US capital gains)',
        'Hold during RNOR (tax-free in India)',
        'Gradual transition over multiple years',
      ],
      requiresProfessionalReview: true,
      reviewType: 'ca',
      factPackSource: 'assets.residentCountry',
    });
  }

  return points;
}

/**
 * Extract items requiring CA review
 */
function extractCAItems(factPack: FactPack): string[] {
  const items: string[] = [];

  factPack.priorityActions
    .filter((a) => a.requiresCAReview)
    .forEach((a) => items.push(a.title));

  factPack.taxStrategy.liquidationPlan
    .filter((l) => l.requiresCAReview)
    .forEach((l) => items.push(l.asset));

  return [...new Set(items)];
}

/**
 * Format month and year for display
 */
function formatMonthYear(month: number, year: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${months[month - 1]} ${year}`;
}
